/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * CanvassView — the canvasser-facing door-knocking cockpit.
 *
 * Owns the full Stage 1 → Stage 4 lifecycle through internal substates.
 * The parent App.tsx only routes us in via `view === 'canvass'`; we
 * handle every transition below that ourselves.
 *
 * Substates (all rendered inside one wrapper div so back-nav is fast
 * and iPad-friendly):
 *
 *   'home'      — list existing shifts + "Start new walk-up shift" CTA
 *   'pick'      — pick a route from GET /routes (filtered by canvasser)
 *   'briefing'  — Stage 1 pre-shift briefing (tier breakdown, ACP flag,
 *                 talking points). Button: "Start knocking".
 *   'stops'     — Stage 2 list of RouteStops. Tap a stop → 'card'. Tap
 *                 "End shift" → 'aar'. Headed by the stat bar (conversations,
 *                 outcome mix, doors left, daylight, pace — orientation, never
 *                 a score) and offering a map lasso that pulls the block you
 *                 are standing in to the front of the working order. The
 *                 reorder is a client-side view; nothing about the route is
 *                 written back (see src/lib/lassoOrder.ts).
 *   'card'      — Stage 3 per-door card. Select outcome + (optionally)
 *                 fill the structured questionnaire, submit → back to
 *                 'stops'.
 *   'aar'       — Stage 4 AAR form. Shows the auto-summary on top, then
 *                 the four reflection textareas. Submit → 'done'.
 *   'done'      — Final confirmation screen. Back → 'home'.
 *
 * The view is ALWAYS live (no mock mode) because creating shifts / writing
 * knocks / persisting AARs is what the whole slice is for. A failed network
 * call surfaces via `formatCanvassError`.
 *
 * Role walls: every endpoint under /canvass accepts CANVASSER+, so this
 * view is safe to expose to bare canvassers. Coordinator+ can use the
 * same view for their own shifts; they get the review surface elsewhere.
 *
 * See memory-bank/canvassLifecyclePlan.md for the five-stage contract.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, Polyline, Popup, TileLayer } from 'react-leaflet';
import FullscreenControl from '@/components/map/FullscreenControl';
import LassoControl, { type LassoLatLng } from '@/components/map/LassoControl';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { daylightRemaining, formatDaylight } from '@/lib/daylight';
import { doorsPerHour, summarizeOutcomes } from '@/lib/shiftStats';
import { orderStopsByLasso, selectStopsInRing } from '@/lib/lassoOrder';

import client from '@/api/client';
import {
  abandonShift,
  createKnock,
  createKnockResilient,
  createShift,
  endShift,
  formatCanvassError,
  getAARPrefill,
  getBriefing,
  listIssues,
  listShiftKnocks,
  listShifts,
  photoUrl,
  sendFieldDeviceBeacon,
  submitAAR,
  uploadKnockPhoto,
} from '@/api/canvass';
import {
  listQueuedKnocksForShift,
  replayAllKnocks,
  subscribe as subscribeOfflineQueue,
  type QueuedKnock,
} from '@/lib/canvassOfflineQueue';
import {
  cacheSlate,
  clearCachedSlate,
  readCachedSlate,
} from '@/lib/slateCache';
import { SlateChip } from '@/components/canvass/SlateChip';
import { RevisitQueuePanel } from '@/components/canvass/RevisitQueuePanel';
import { YourCampaignsCard } from '@/components/YourCampaignsCard';
import {
  CandidateLeanStepper,
  STEPPER_LEAN_SOURCE,
} from '@/components/canvass/CandidateLeanStepper';
import { candidateShortName, type CandidateBrief } from '@/types/candidate';
// Per-candidate lean union (slate variant), distinct from the scalar
// `CandidateLean` imported from '@/types/canvass' below.
import type { CandidateLean as SlateLean } from '@/api/leans';
import type {
  AARAutoSummary,
  AARPrefill,
  AARRead,
  AnsweredBy,
  CampaignIssueRead,
  CandidateLean,
  CanvassShiftRead,
  KnockCreate,
  KnockRead,
  KnockResponseCreate,
  ShiftBriefing,
  VoteIntent,
} from '@/types/canvass';
import type { KnockbackOutcome } from '@/types';
import { InfoTip } from '@/components/InfoTip';
import {
  OnboardingModal,
  OnboardingHelpButton,
  hasSeenOnboarding,
} from '@/components/OnboardingModal';
import { CoachMarks } from '@/components/onboarding/CoachMarks';
import {
  CANVASSER_HOME_TOUR,
  CANVASSER_STOPS_TOUR,
  CANVASSER_CARD_TOUR,
  TOURS,
} from '@/components/onboarding/tours';
import { useOnboarding } from '@/hooks/useOnboarding';
import { TableRowSkeleton } from '@/components/Skeleton';

// ---------------------------------------------------------------------------
// Small local route + stop shapes — mirrors what GET /routes/{id} returns
// ---------------------------------------------------------------------------

interface LightRoute {
  id: number;
  name: string;
  door_count: number;
  status: string;
}

interface StopVoter {
  id: number;
  first_name: string;
  last_name: string;
  address: string;
  city?: string | null;
  zip_code?: string | null;
  // `lat` / `lng` are populated by the backend `/routes/{id}` endpoint
  // (see backend/app/routers/routes.py ~line 207). They can be null for
  // ungeocoded rows, which is why the map surface filters them out
  // before rendering markers rather than trusting every stop has a
  // position.
  lat?: number | null;
  lng?: number | null;
  score?: number | null;
  engagement_tier?: string | null;
  party?: string | null;
}

interface Stop {
  id: number;
  sequence: number;
  status: string;
  voter: StopVoter;
}

// ---------------------------------------------------------------------------
// Visual helpers
// ---------------------------------------------------------------------------

// Semantic mapping — kept in sync with HeatmapDetailDrawer's outcomeBadgeClass:
//   REFUSED / COME_BACK → warning (soft rejection, deferred contact)
//   MOVED               → info (neutral fact)
//   DO_NOT_CONTACT      → danger (active exclusion)
const OUTCOMES: { value: KnockbackOutcome; label: string; color: string }[] = [
  { value: 'talked', label: 'Talked', color: 'bg-status-success-bg hover:bg-status-success-bg/85' },
  { value: 'not_home', label: 'Not home', color: 'bg-bg-elevated hover:bg-border-subtle' },
  { value: 'refused', label: 'Refused', color: 'bg-status-warning-bg hover:bg-status-warning-bg/85' },
  { value: 'moved', label: 'Moved', color: 'bg-status-info-bg hover:bg-status-info-bg/85' },
  { value: 'come_back', label: 'Come back', color: 'bg-status-warning-bg hover:bg-status-warning-bg/85' },
  { value: 'do_not_contact', label: 'Do NOT contact', color: 'bg-status-danger-bg hover:bg-status-danger-bg/85' },
];

function StatusPill({ status }: { status: string }) {
  const colors: Record<string, string> = {
    planned: 'bg-bg-elevated text-fg-secondary',
    in_progress: 'bg-status-success-bg text-status-success',
    completed: 'bg-status-info-bg text-status-info',
    abandoned: 'bg-status-warning-bg text-status-warning',
  };
  const cls = colors[status] || 'bg-bg-elevated text-fg-secondary';
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${cls}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

function ErrorBanner({ message, onDismiss }: { message: string; onDismiss?: () => void }) {
  return (
    <div className="rounded border border-status-danger bg-status-danger-bg/40 px-4 py-3 text-status-danger flex items-center justify-between">
      <div>{message}</div>
      {onDismiss && (
        <button onClick={onDismiss} className="text-status-danger hover:text-fg-primary text-xs ml-4">
          DISMISS
        </button>
      )}
    </div>
  );
}

function Loading({ label = 'Loading…' }: { label?: string }) {
  return <div className="text-fg-muted py-4 text-center text-sm italic">{label}</div>;
}

function formatTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

type Phase = 'home' | 'pick' | 'briefing' | 'stops' | 'card' | 'aar' | 'done';

export function CanvassView({ onBack }: { onBack: () => void }) {
  // top-level state machine
  const [phase, setPhase] = useState<Phase>('home');
  const [error, setError] = useState<string | null>(null);
  const [forceOnboard, setForceOnboard] = useState(false);

  // Guided coach-mark tours (server-persisted "seen" state) — one short tour
  // per phase rather than a single linear tour spanning the whole lifecycle.
  // That's deliberate: a step can only anchor to an element that's live in the
  // DOM, and the door/outcome controls don't exist until a shift is running.
  // Per-phase tours give the same "advance as you walk" progression with every
  // anchor guaranteed present. 'home' additionally sits behind the
  // OnboardingModal (explain → then guide); 'stops'/'card' have no modal and
  // fire directly on first arrival.
  const { hasSeen, markSeen, seen } = useOnboarding();
  const [activeTourId, setActiveTourId] = useState<string | null>(null);
  const onboardingDisabled =
    typeof window !== 'undefined' &&
    ((window as any).__PCGOP_DISABLE_ONBOARDING__ === true ||
      (import.meta as any).env?.VITE_DISABLE_ONBOARDING === '1');

  // Which tour belongs to which phase. Phases without an entry ('pick',
  // 'briefing', 'aar', 'done') simply never start one.
  const phaseTourId: Partial<Record<Phase, string>> = {
    home: CANVASSER_HOME_TOUR.id,
    stops: CANVASSER_STOPS_TOUR.id,
    card: CANVASSER_CARD_TOUR.id,
  };

  // Start the current phase's tour the first time this user reaches it.
  // 'home' is the exception: on a genuine first visit the modal is showing, so
  // its onClose hands off to the tour (below). We only auto-start home here
  // when the modal was already dismissed on a prior visit — e.g. the tour
  // shipped after they'd seen the modal — so the two never overlap.
  useEffect(() => {
    if (onboardingDisabled) return;
    const tourId = phaseTourId[phase];
    if (!tourId || hasSeen(tourId)) return;
    if (phase === 'home') {
      if (hasSeenOnboarding('canvassview')) setActiveTourId(tourId);
      return;
    }
    setActiveTourId(tourId);
    // `seenKey` is in the deps so this re-evaluates when the authenticated
    // user changes or /auth/me resolves — otherwise a view mounted before the
    // user loaded (or an account switch) would never start its tour, or would
    // start one the user had already completed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, seen.join('|')]);

  // data loaded per-phase
  const [shifts, setShifts] = useState<CanvassShiftRead[] | null>(null);
  const [routes, setRoutes] = useState<LightRoute[] | null>(null);
  const [activeShift, setActiveShift] = useState<CanvassShiftRead | null>(null);
  const [briefing, setBriefing] = useState<ShiftBriefing | null>(null);
  // Slate recovered from localStorage when the briefing fetch failed (reload
  // on a dead network). Only consulted as a fallback — see `slateForWalk`.
  const [cachedSlate, setCachedSlate] = useState<CandidateBrief[] | null>(null);
  // Cached alongside the slate so an offline reload can still tell "feature unused"
  // (stay silent) from "this route resolved nobody" (shout). See SlateChip.
  const [cachedConfigured, setCachedConfigured] = useState<boolean | null>(null);
  const [stops, setStops] = useState<Stop[] | null>(null);
  const [knocks, setKnocks] = useState<KnockRead[] | null>(null);
  const [currentStop, setCurrentStop] = useState<Stop | null>(null);
  const [issues, setIssues] = useState<CampaignIssueRead[] | null>(null);
  const [aarPrefill, setAarPrefill] = useState<AARPrefill | null>(null);
  const [submittedAAR, setSubmittedAAR] = useState<AARRead | null>(null);

  // Offline-queue state: the per-shift set of knocks that were written to
  // IndexedDB because the network was unreachable at the time. These rows
  // get merged into `knockedStopIds` so the stops list stripes them as
  // "knocked" even before they have been drained to the backend. A manual
  // "Retry now" button and an automatic `online` event listener both call
  // `doReplay()` to drain the queue.
  const [queuedKnocks, setQueuedKnocks] = useState<QueuedKnock[]>([]);
  const [replaying, setReplaying] = useState(false);
  const [replayBanner, setReplayBanner] = useState<string | null>(null);

  const refreshQueue = async () => {
    if (!activeShift) {
      setQueuedKnocks([]);
      return;
    }
    try {
      const rows = await listQueuedKnocksForShift(activeShift.id);
      setQueuedKnocks(rows);
    } catch {
      // IDB unavailable (SSR / incognito lockdown) — treat as empty.
      setQueuedKnocks([]);
    }
  };

  const doReplay = async () => {
    if (!activeShift || replaying) return;
    setReplaying(true);
    setReplayBanner(null);
    try {
      const result = await replayAllKnocks((sid, body) =>
        createKnock(sid, body),
      );
      if (result.succeeded > 0) {
        setReplayBanner(
          `Flushed ${result.succeeded} queued knock${
            result.succeeded === 1 ? '' : 's'
          } to the server.`,
        );
        // Re-pull knocks from the server so the list reflects truth.
        try {
          const fresh = await listShiftKnocks(activeShift.id);
          setKnocks(fresh);
        } catch {
          /* non-fatal — the next `stops` re-entry will retry */
        }
      }
      if (result.failed > 0 && result.lastError) {
        setReplayBanner(
          `Still ${result.failed} queued (${result.lastError}).`,
        );
      }
    } catch (e) {
      setReplayBanner(formatCanvassError(e));
    } finally {
      setReplaying(false);
      await refreshQueue();
    }
  };

  // Load the queue whenever the active shift changes, and subscribe to
  // queue-change events from other parts of the app (e.g. a knock save
  // that fell back to the queue).
  useEffect(() => {
    refreshQueue();
    const unsub = subscribeOfflineQueue(() => {
      refreshQueue();
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeShift?.id]);

  // Auto-replay the queue the moment the browser reports it's back online.
  // This is the "canvasser walks from a dead zone back into LTE" path —
  // the user doesn't need to tap anything, the queue just drains.
  useEffect(() => {
    function onOnline() {
      if (activeShift) doReplay();
    }
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeShift?.id]);

  // Beacon emitter — tell the backend how many knocks are queued in IDB
  // so coordinators can see field-device health in the AAR Inbox.
  // Fires on mount, on queue-count change, and every 60 s while a shift
  // is active.  Fire-and-forget — never blocks the canvasser.
  useEffect(() => {
    if (!activeShift) return;
    const fire = () => {
      sendFieldDeviceBeacon({
        shift_id: activeShift.id,
        queued_count: queuedKnocks.length,
      });
    };
    fire(); // immediate on mount / count change
    const iv = setInterval(fire, 60_000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeShift?.id, queuedKnocks.length]);

  // --- Phase: 'home' -----------------------------------------------------
  useEffect(() => {
    if (phase !== 'home') return;
    let cancelled = false;
    listShifts({ limit: 20 })
      .then((rows) => {
        if (!cancelled) setShifts(rows);
      })
      .catch((e) => {
        if (!cancelled) setError(formatCanvassError(e));
      });
    return () => {
      cancelled = true;
    };
  }, [phase]);

  function goPickRoute() {
    setError(null);
    setRoutes(null);
    setPhase('pick');
  }

  // --- Phase: 'pick' -----------------------------------------------------
  useEffect(() => {
    if (phase !== 'pick') return;
    let cancelled = false;
    client
      .get('/routes')
      .then((res) => {
        if (!cancelled) setRoutes(res.data || []);
      })
      .catch((e) => {
        if (!cancelled) setError(formatCanvassError(e));
      });
    return () => {
      cancelled = true;
    };
  }, [phase]);

  async function startWalkUp(routeId: number) {
    setError(null);
    try {
      const shift = await createShift({ route_id: routeId, start_now: true });
      setActiveShift(shift);
      setPhase('briefing');
    } catch (e) {
      setError(formatCanvassError(e));
    }
  }

  // --- Briefing load -----------------------------------------------------
  // Keyed on `activeShift`, NOT on `phase === 'briefing'`. Two reasons, both
  // about the slate (plan §8.1.1):
  //
  //  1. Resuming an in-progress shift jumps straight to 'stops' (see
  //     HomeScreen's onResume), skipping the briefing phase entirely. Gated on
  //     the phase, the briefing would never load on that path and the slate
  //     chip would be blank for the whole walk — for the volunteer who is
  //     already out there, which is the worst case to miss.
  //  2. The slate has to be in hand BEFORE the door, not fetched at it. Load
  //     it once, up front, while there's still signal; the chip renders from
  //     state forever after.
  useEffect(() => {
    if (!activeShift) return;
    let cancelled = false;
    setBriefing(null);
    Promise.all([
      getBriefing(activeShift.id),
      listIssues().catch(() => [] as CampaignIssueRead[]),
    ])
      .then(([b, iss]) => {
        if (cancelled) return;
        setBriefing(b);
        setIssues(iss);
        // Survive a reload on a dead network. Fresh server data always wins;
        // this is only ever read when the fetch below fails.
        cacheSlate(
          activeShift.id,
          b.candidates || [],
          b.slate_names || '',
          b.slate_configured === true,
        );
      })
      .catch((e) => {
        if (cancelled) return;
        // Offline (or the server is down) and we may have walked this shift
        // before: fall back to the cached slate so the chip still answers
        // "who are we knocking for". We deliberately do NOT synthesize a whole
        // ShiftBriefing here — door counts and ACP flags must not be served
        // from a stale cache, and pretending we have them would be a worse lie
        // than admitting the briefing didn't load.
        const cached = readCachedSlate(activeShift.id);
        if (cached) {
          setCachedSlate(cached.candidates);
          setCachedConfigured(cached.slate_configured);
        }
        setError(formatCanvassError(e));
      });
    return () => {
      cancelled = true;
    };
  }, [activeShift]);

  function goToStops() {
    setError(null);
    setPhase('stops');
  }

  // The slate to show for this walk. Server response first, localStorage
  // fallback second, `null` (= "not loaded") only when we have neither.
  //
  // ⚠️ `null` and `[]` are NOT interchangeable here. `[]` is a real, measured
  // answer — 213 of 7,543 routes have no precinct to derive a slate from — and
  // it must reach SlateChip as `[]` so the chip can shout "No candidates
  // assigned". Writing `briefing?.candidates || null` instead of this would
  // turn every legitimately-empty slate into a silent no-render: exactly the
  // dead-honesty bug plan §3.5 calls out.
  const slateForWalk: CandidateBrief[] | null = briefing
    ? briefing.candidates || []
    : cachedSlate;

  // Has the org adopted candidates at all? Only meaningful when the slate is
  // empty: false keeps the chip silent (nothing is configured, so nothing is
  // missing), true makes the empty state shout. Defaults TRUE when we genuinely
  // don't know — an unknown is likelier to be a load failure than an
  // unconfigured tenant, and a spurious "check with your coordinator" is a
  // cheaper mistake than silently hiding a route with no slate.
  const slateConfigured: boolean = briefing
    ? briefing.slate_configured === true
    : cachedConfigured ?? true;

  // --- Phase: 'stops' ----------------------------------------------------
  // Pull route stops + any already-recorded knocks so we can stripe the list
  useEffect(() => {
    if (phase !== 'stops' || !activeShift) return;
    let cancelled = false;
    setStops(null);
    Promise.all([
      client.get(`/routes/${activeShift.route_id}`),
      listShiftKnocks(activeShift.id),
    ])
      .then(([routeRes, ks]) => {
        if (cancelled) return;
        setStops(routeRes.data?.stops || []);
        setKnocks(ks);
      })
      .catch((e) => {
        if (!cancelled) setError(formatCanvassError(e));
      });
    return () => {
      cancelled = true;
    };
  }, [phase, activeShift]);

  // Fast lookup: which route_stop_ids have been knocked this shift?
  // Merges server-persisted knocks with offline-queued knocks so the UI
  // stripes both as "knocked" — the canvasser should never see a door
  // they just logged show up as un-knocked.
  const knockedStopIds = useMemo(() => {
    const s = new Set<number>();
    (knocks || []).forEach((k) => s.add(k.route_stop_id));
    queuedKnocks.forEach((q) => s.add(q.body.route_stop_id));
    return s;
  }, [knocks, queuedKnocks]);

  function openCard(stop: Stop) {
    setCurrentStop(stop);
    setPhase('card');
  }

  async function endCurrentShift() {
    if (!activeShift) return;
    setError(null);
    try {
      const updated = await endShift(activeShift.id);
      // The slate describes a walk in progress; it has no meaning once the
      // shift is closed. Drop it so a future shift can never read a prior
      // walk's slate out of localStorage.
      clearCachedSlate(activeShift.id);
      setActiveShift(updated);
      setPhase('aar');
    } catch (e) {
      setError(formatCanvassError(e));
    }
  }

  async function abandonCurrentShift(reason: string) {
    if (!activeShift) return;
    setError(null);
    try {
      const updated = await abandonShift(activeShift.id, { reason });
      clearCachedSlate(activeShift.id);
      setActiveShift(updated);
      setPhase('aar');
    } catch (e) {
      setError(formatCanvassError(e));
    }
  }

  // --- Phase: 'aar' ------------------------------------------------------
  useEffect(() => {
    if (phase !== 'aar' || !activeShift) return;
    let cancelled = false;
    setAarPrefill(null);
    getAARPrefill(activeShift.id)
      .then((p) => {
        if (!cancelled) setAarPrefill(p);
      })
      .catch((e) => {
        if (!cancelled) setError(formatCanvassError(e));
      });
    return () => {
      cancelled = true;
    };
  }, [phase, activeShift]);

  async function onKnockSaved(savedKnock: KnockRead) {
    // Append into our in-memory list so the stop flips to "knocked" on
    // return to the stops screen without a round trip.
    setKnocks((prev) => (prev ? [savedKnock, ...prev] : [savedKnock]));
    setCurrentStop(null);
    setPhase('stops');
  }

  async function onAARSubmitted(aar: AARRead) {
    setSubmittedAAR(aar);
    setPhase('done');
  }

  function resetToHome() {
    setActiveShift(null);
    setBriefing(null);
    setStops(null);
    setKnocks(null);
    setCurrentStop(null);
    setAarPrefill(null);
    setSubmittedAAR(null);
    setError(null);
    setPhase('home');
  }

  // ---- render ----------------------------------------------------------

  return (
    <div className="space-y-4">
      <OnboardingModal
        viewKey="canvassview"
        title="Door Canvass"
        intro="The canvasser-facing cockpit. Pick a walking route, get a quick briefing on who's behind each door, knock, log the outcome, and file an after-action report when you're done."
        termIds={[
          'canvass_shift',
          'briefing',
          'talking_points',
          'stops_map',
          'knock',
          'outcome_categories',
          'aar',
          'friendlies_first',
          'acp_voter',
          'candidate_lean',
          'vote_intent',
          'door_photo',
        ]}
        workflows={[
          'Start a walk-up shift right from the field — no pre-planning required.',
          'Read the briefing before walking: tier mix, ACP flags, talking points.',
          'Walk the turf on the map view; tap a pin to log an outcome.',
          'Need finer detail? Toggle to the list view for addresses and ungeocoded stops.',
          'Snap a door photo on the per-door card — EXIF is stripped server-side.',
          'End the shift to fill the AAR — honest reflections beat perfect numbers.',
        ]}
        forceOpen={forceOnboard}
        onClose={() => {
          const wasForced = forceOnboard;
          setForceOnboard(false);
          // First-visit dismissal (NOT a Help-button reopen) hands off to the
          // guided tour. A forced reopen just closes.
          if (!wasForced && !onboardingDisabled && !hasSeen(CANVASSER_HOME_TOUR.id)) {
            setActiveTourId(CANVASSER_HOME_TOUR.id);
          }
        }}
      />

      {/* Only render a tour while we're on the phase it was written for — if
          the canvasser navigates on mid-tour, its anchors are gone, so it
          hides rather than chasing missing targets. It re-offers next time
          they hit that phase (an unfinished tour is never marked seen). */}
      {activeTourId && phaseTourId[phase] === activeTourId && TOURS[activeTourId] && (
        <CoachMarks
          tour={TOURS[activeTourId]}
          onFinish={() => {
            const finishedId = activeTourId;
            setActiveTourId(null);
            void markSeen(finishedId);
          }}
          // Anchors never mounted (view still loading, or they navigated on).
          // Hide WITHOUT marking seen so it re-offers next time.
          onAbort={() => setActiveTourId(null)}
        />
      )}

      <div className="flex items-center justify-between">
        <div />
        <h1 className="text-2xl font-bold text-fg-primary flex items-center gap-2">
          Door Canvass <InfoTip termId="canvass_shift" />
        </h1>
        <div className="w-28 flex justify-end" data-tour="onboarding-help">
          <OnboardingHelpButton onClick={() => setForceOnboard(true)} />
        </div>
      </div>

      {/*
        WHO ARE WE KNOCKING FOR (plan §8.2 tier 3).

        Its own row under the view header rather than inside the title row:
        at 375px the title row is already `[spacer | Door Canvass | help]`, and
        squeezing a third element in there is precisely how the AppShell
        `min-w-0` truncation bug got made. A full-width row is honest about the
        space it needs, and the chip is no less persistent for it.

        Mounted for every walk phase — briefing, stops, and card — so it is on
        screen continuously from "start knocking" to the last door. Not on
        'home'/'pick' (no shift yet = no slate to name) and not on 'aar'/'done'
        (the walk is over).
      */}
      {activeShift && (phase === 'briefing' || phase === 'stops' || phase === 'card') && (
        <SlateChip
          candidates={slateForWalk}
          slateConfigured={slateConfigured}
          className="w-full"
        />
      )}

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {replayBanner && (
        <div className="bg-bg-raised border border-border-subtle rounded p-3 text-sm text-fg-secondary flex items-center justify-between gap-3">
          <span>📡 {replayBanner}</span>
          <button
            onClick={() => setReplayBanner(null)}
            className="text-fg-muted hover:text-fg-primary text-xs"
          >
            Dismiss
          </button>
        </div>
      )}

      {phase === 'home' && (
        <HomeScreen shifts={shifts} onStart={goPickRoute} onResume={(s) => {
          setActiveShift(s);
          setPhase(s.status === 'in_progress' ? 'stops' : 'briefing');
        }} onStartRoute={startWalkUp} />
      )}

      {phase === 'pick' && (
        <RoutePickerScreen routes={routes} onPick={startWalkUp} />
      )}

      {phase === 'briefing' && activeShift && (
        <BriefingScreen briefing={briefing} onStart={goToStops} />
      )}


      {phase === 'stops' && activeShift && (
        <StopsScreen
          shift={activeShift}
          stops={stops}
          knockedStopIds={knockedStopIds}
          knocks={knocks || []}
          queuedKnocks={queuedKnocks}
          replaying={replaying}
          onRetryQueue={doReplay}
          onPick={openCard}
          onEndShift={endCurrentShift}
          onAbandon={abandonCurrentShift}
        />
      )}

      {phase === 'card' && activeShift && currentStop && (
        <PerDoorCard
          shiftId={activeShift.id}
          stop={currentStop}
          issues={issues || []}
          slate={slateForWalk || []}
          onCancel={() => {
            setCurrentStop(null);
            setPhase('stops');
          }}
          onSaved={onKnockSaved}
          onError={(m) => setError(m)}
        />
      )}

      {phase === 'aar' && activeShift && (
        <AARScreen
          shift={activeShift}
          prefill={aarPrefill}
          onSubmitted={onAARSubmitted}
          onError={(m) => setError(m)}
        />
      )}

      {phase === 'done' && submittedAAR && (
        <DoneScreen aar={submittedAAR} onBackHome={resetToHome} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase components
// ---------------------------------------------------------------------------

function HomeScreen({
  shifts,
  onStart,
  onResume,
  onStartRoute,
}: {
  shifts: CanvassShiftRead[] | null;
  onStart: () => void;
  onResume: (s: CanvassShiftRead) => void;
  // Start a walk-up shift on a specific route — how the revisit queue turns a
  // door into an actual walk. Same handler the route picker uses.
  onStartRoute?: (routeId: number) => void;
}) {
  const openShifts = (shifts || []).filter(
    (s) => s.status === 'in_progress' || s.status === 'planned',
  );
  return (
    <div className="space-y-4">
      <div className="bg-bg-raised rounded-lg border border-border-base p-6 flex items-center justify-between">
        <div>
          <div className="text-xs text-fg-muted uppercase tracking-wide">Walk-up</div>
          <h2 className="text-xl font-bold text-fg-primary mt-1">Start a new canvass shift</h2>
          <p className="text-fg-muted text-sm mt-1">
            Pick a route, hit the briefing, start knocking.
          </p>
        </div>
        <button
          onClick={onStart}
          data-tour="new-shift"
          className="bg-accent hover:bg-accent-hover text-white font-bold px-6 py-3 rounded-lg"
        >
          New shift →
        </button>
      </div>

      {/*
        The revisit queue sits BELOW the start CTA and ABOVE the shift history:
        it is the reason to start a walk, not a report on walks already done.
        It renders nothing while it loads, so the button above it never moves,
        and it is a card rather than a modal — a volunteer who wants to walk a
        fresh route should be able to scroll straight past it.
      */}
      <RevisitQueuePanel onStartRoute={onStartRoute} />

      {/* The canvasser's ONLY view of which races they work — HomeView, where
          this card also lives, is unreachable for them (homeViewForRole sends
          a canvasser straight to their doors). Below the two action panels
          because it is context, not a thing to do. */}
      <YourCampaignsCard />

      <div className="bg-bg-raised rounded-lg border border-border-base p-4" data-tour="recent-shifts">
        <h3 className="text-sm font-semibold text-fg-secondary uppercase tracking-wide mb-2">
          Your recent shifts
        </h3>
        {shifts === null ? (
          <Loading />
        ) : shifts.length === 0 ? (
          <div className="text-fg-muted text-sm italic py-2">No shifts yet.</div>
        ) : (
          <ul className="divide-y divide-border-base">
            {shifts.map((s) => {
              const resumable = s.status === 'in_progress' || s.status === 'planned';
              return (
                <li key={s.id} className="py-2 flex items-center justify-between">
                  <div>
                    <div className="text-fg-primary text-sm font-medium">Shift #{s.id}</div>
                    <div className="text-xs text-fg-muted">
                      Route {s.route_id} · started {formatTime(s.started_at)} · ended {formatTime(s.ended_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusPill status={s.status} />
                    {resumable && (
                      <button
                        onClick={() => onResume(s)}
                        className="bg-bg-elevated hover:bg-border-subtle text-fg-primary text-xs font-semibold px-3 py-1 rounded inline-flex items-center justify-center min-h-11 sm:min-h-0"
                      >
                        Resume →
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {openShifts.length > 0 && (
          <div className="mt-3 text-[11px] text-status-warning">
            You have {openShifts.length} open shift{openShifts.length === 1 ? '' : 's'} — resume before starting a new one.
          </div>
        )}
      </div>
    </div>
  );
}

function RoutePickerScreen({
  routes,
  onPick,
}: {
  routes: LightRoute[] | null;
  onPick: (id: number) => void;
}) {
  if (routes === null) return <Loading label="Loading your routes…" />;
  if (routes.length === 0) {
    return (
      <div className="bg-bg-raised rounded-lg border border-border-base p-6 text-fg-secondary">
        No routes assigned to you. Ask a coordinator to assign a route.
      </div>
    );
  }
  return (
    <div className="bg-bg-raised rounded-lg border border-border-base p-4">
      <h3 className="text-sm font-semibold text-fg-secondary uppercase tracking-wide mb-3">
        Pick a route
      </h3>
      <ul className="divide-y divide-border-base">
        {routes.map((r) => (
          <li key={r.id} className="py-3 flex items-center justify-between gap-4">
            <div>
              <div className="text-fg-primary font-medium">{r.name}</div>
              <div className="text-xs text-fg-muted">
                {r.door_count} doors · {r.status}
              </div>
            </div>
            <button
              onClick={() => onPick(r.id)}
              className="bg-accent hover:bg-accent-hover text-white text-sm font-semibold px-4 py-2 rounded"
            >
              Start →
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BriefingScreen({
  briefing,
  onStart,
}: {
  briefing: ShiftBriefing | null;
  onStart: () => void;
}) {
  if (briefing === null) return <Loading label="Loading briefing…" />;
  return (
    <div className="space-y-4">
      <div className="bg-bg-raised rounded-lg border border-border-base p-6">
        <div className="text-xs text-fg-muted uppercase tracking-wide">Stage 1 — briefing</div>
        <h2 className="text-2xl font-bold text-fg-primary mt-1">{briefing.route_name}</h2>
        {/*
          Tier 1 of plan §8.2 — the slate, before the walk. Placed directly
          under the route name and ABOVE the door counts on purpose: who you
          are knocking for outranks how many doors there are.

          The `{slate_names}` line under it is Phase 1.5 — the sentence the
          volunteer actually says out loud. Plan §8.2: "Tier 5 is the one that
          actually works... A volunteer cannot read that aloud and not know who
          they're canvassing for."
        */}
        <div className="mt-4">
          <div className="text-xs text-fg-muted uppercase tracking-wide mb-2">
            Who you're knocking for
          </div>
          {briefing.candidates.length === 0 ? (
            // The loud empty state. NOT a hidden section — a volunteer who
            // sees nothing here must know it's missing, not conclude there was
            // nothing to see (plan §3.5).
            <div className="rounded border border-status-warning bg-status-warning-bg/30 p-3 text-status-warning text-sm">
              <strong className="font-semibold">
                ⚠ No candidates assigned to this route
              </strong>
              <div className="mt-1 text-status-warning/90">
                Check with your coordinator before you start knocking — you
                should know who you're canvassing for.
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {briefing.candidates.map((c) => (
                  <div
                    key={c.id}
                    className="rounded border border-border-base bg-bg-base/60 p-3"
                  >
                    <div className="text-sm font-bold text-fg-primary">
                      {c.full_name}
                    </div>
                    <div className="text-xs text-fg-muted mt-0.5">
                      {c.office}
                      {c.party ? ` · ${c.party}` : ''}
                    </div>
                  </div>
                ))}
              </div>
              {briefing.slate_names && (
                <div className="mt-2 rounded border border-accent/40 bg-accent/5 p-3">
                  <div className="text-[10px] uppercase tracking-wide text-fg-muted mb-1">
                    Say this at the door
                  </div>
                  <p className="text-sm text-fg-secondary italic">
                    "Hi, I'm with {briefing.slate_names}."
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="mt-4 grid grid-cols-3 gap-4">
          <div className="bg-bg-base rounded p-4">
            <div className="text-xs text-fg-muted uppercase">Doors</div>
            <div className="text-3xl font-bold text-fg-primary mt-1">{briefing.total_doors}</div>
          </div>
          <div className="bg-bg-base rounded p-4">
            <div className="text-xs text-fg-muted uppercase">ACP voters</div>
            <div className="text-3xl font-bold text-status-warning mt-1">{briefing.acp_voter_count}</div>
          </div>
          <div className="bg-bg-base rounded p-4">
            <div className="text-xs text-fg-muted uppercase">Moved</div>
            <div className="text-3xl font-bold text-fg-secondary mt-1">{briefing.moved_voter_count}</div>
          </div>
        </div>

        {briefing.tier_breakdown.length > 0 && (
          <div className="mt-4">
            <div className="text-xs text-fg-muted uppercase tracking-wide mb-1">Tier breakdown</div>
            <div className="flex flex-wrap gap-2">
              {briefing.tier_breakdown.map((t) => (
                <span
                  key={t.tier}
                  className="bg-bg-base text-fg-secondary rounded px-2 py-1 text-xs border border-border-base"
                >
                  {t.tier}: <strong className="text-fg-primary">{t.count}</strong>
                </span>
              ))}
            </div>
          </div>
        )}

        {briefing.acp_voter_count > 0 && (
          <div className="mt-4 rounded border border-status-warning bg-status-warning-bg/30 p-3 text-status-warning text-sm">
            ⚠ This route includes {briefing.acp_voter_count} Safe-at-Home (ACP) voter
            {briefing.acp_voter_count === 1 ? '' : 's'}. Do not knock — leave literature only.
          </div>
        )}

        {briefing.talking_points.length > 0 ? (
          <div className="mt-4">
            <div className="text-xs text-fg-muted uppercase tracking-wide mb-2">
              Talking points
            </div>
            <div className="space-y-2">
              {briefing.talking_points.map((tp) => (
                <div
                  key={tp.id}
                  className="rounded border border-border-base bg-bg-base/60 p-3"
                >
                  <div className="text-sm font-semibold text-accent-fg">
                    {tp.title}
                  </div>
                  <div className="mt-1 text-sm text-fg-secondary whitespace-pre-wrap leading-snug">
                    {tp.body}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-4 rounded border border-border-base bg-bg-base/40 p-3 text-xs text-fg-muted italic">
            No talking points authored yet. A coordinator can add them from
            the Talking Points admin screen so the next shift briefing
            carries script guidance.
          </div>
        )}
      </div>

      <button
        onClick={onStart}
        className="w-full bg-accent hover:bg-accent-hover text-white font-bold py-4 rounded-lg text-lg"
      >
        Start knocking →
      </button>
    </div>
  );
}

function StopsScreen({
  shift,
  stops,
  knockedStopIds,
  knocks,
  queuedKnocks,
  replaying,
  onRetryQueue,
  onPick,
  onEndShift,
  onAbandon,
}: {
  shift: CanvassShiftRead;
  stops: Stop[] | null;
  knockedStopIds: Set<number>;
  knocks: KnockRead[];
  /** Offline rows still in IndexedDB. Needed whole, not just counted: they
   *  carry outcomes, and the stat bar has to count them or it disagrees with
   *  the door count beside it (which already merges them). */
  queuedKnocks: QueuedKnock[];
  replaying: boolean;
  onRetryQueue: () => void;
  onPick: (s: Stop) => void;
  onEndShift: () => void;
  onAbandon: (reason: string) => void;
}) {
  const [abandonOpen, setAbandonOpen] = useState(false);
  const [abandonReason, setAbandonReason] = useState('');
  // Default view is MAP if we have any geocoded stops — a visual turf is
  // almost always more useful than a text list for the walking phase —
  // but fall back to 'list' automatically when nothing is geocodable.
  const [mapMode, setMapMode] = useState<'list' | 'map'>('map');

  // --- Lasso: "I'm standing here, do these next" -------------------------
  // Armed explicitly from the map's lasso button; LassoControl self-disarms
  // after one draw so the next drag pans again. The selection is a set of
  // stop ids and a display order — nothing here is written anywhere. See
  // src/lib/lassoOrder.ts for why this stays client-side.
  const [lassoArmed, setLassoArmed] = useState(false);
  const [lassoPolygon, setLassoPolygon] = useState<LassoLatLng[] | null>(null);
  const [lassoSelection, setLassoSelection] = useState<Set<number>>(
    () => new Set<number>(),
  );

  // Wall-clock read once per render. Pace and daylight both derive from it,
  // and taking one timestamp keeps them describing the same instant.
  const now = new Date();

  // The stat bar's numbers. Both memos are cheap at route scale; they exist
  // so a knock in a dead zone doesn't re-derive the whole mix on every
  // keystroke elsewhere in the tree.
  const mix = useMemo(
    () =>
      summarizeOutcomes(
        knocks,
        queuedKnocks.map((q) => q.body),
      ),
    [knocks, queuedKnocks],
  );
  const daylight = useMemo(
    () => daylightRemaining((stops || []).map((s) => s.voter), now),
    // `now` is intentionally not a dep: it changes identity every render and
    // sunset does not move within a shift. Re-derived when the stops load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stops],
  );

  if (stops === null) return <TableRowSkeleton rows={8} cols={3} />;

  const queuedCount = queuedKnocks.length;
  const knocked = stops.filter((s) => knockedStopIds.has(s.id)).length;
  const remaining = stops.length - knocked;
  const pace = doorsPerHour(knocked, shift.started_at, now);

  // Ordered working list. With an empty selection this returns the array
  // unchanged, which is exactly what "Clear" has to restore.
  const orderedStops = orderStopsByLasso(stops, lassoSelection);
  const lassoKnockedSince = [...lassoSelection].filter((id) =>
    knockedStopIds.has(id),
  ).length;
  const unmappedCount = stops.filter(
    (s) =>
      typeof s.voter.lat !== 'number' ||
      typeof s.voter.lng !== 'number' ||
      (s.voter.lat === 0 && s.voter.lng === 0),
  ).length;

  function handleLassoComplete(ring: LassoLatLng[]) {
    setLassoPolygon(ring);
    // Knocked doors are excluded at selection time, not at render time: the
    // count on the banner is then the number of doors actually pulled forward.
    setLassoSelection(selectStopsInRing(stops || [], ring, knockedStopIds));
  }

  function clearLasso() {
    setLassoPolygon(null);
    setLassoSelection(new Set<number>());
  }
  const geocodableCount = stops.filter(
    (s) => typeof s.voter.lat === 'number' && typeof s.voter.lng === 'number',
  ).length;
  // Hide the toggle entirely if zero stops carry coordinates — no point
  // offering a map that has nothing to render.
  const canShowMap = geocodableCount > 0;
  const effectiveMode: 'list' | 'map' = canShowMap ? mapMode : 'list';
  return (
    <div className="space-y-4">
      {/*
        The stat bar. Read the order of the three lines as the argument:

          1. CONVERSATIONS first, doors second. Doors is effort; a shift is
             judged on whether anybody was home and talked. "12 / 40 doors"
             as the headline reads as a 30% failure even when twelve doors
             produced five real conversations, which is a good hour.
          2. THE MIX, so the headline can't be mistaken for the whole story —
             not-homes are stated out loud rather than hidden in the gap
             between two numbers.
          3. WHAT'S LEFT, in doors and in light. Daylight is the binding
             constraint on canvassing, and "14 left, ~50 min of light" is the
             only number here that changes what a volunteer does next.

        Deliberately absent: any percentage of a target, any streak, any goal
        line, any comparison to another volunteer, any encouragement. This is
        orientation, not a score. A bar that reads as a target teaches the one
        behavior that would poison every downstream number — logging a
        not-home as a contact to make the bar move.
      */}
      <div
        className="bg-bg-raised rounded-lg border border-border-base p-4 flex items-start justify-between gap-3"
        data-tour="stops-progress"
      >
        <div className="min-w-0">
          <div className="text-xs text-fg-muted uppercase">Shift #{shift.id}</div>
          <div className="text-fg-primary text-lg font-bold flex items-baseline gap-x-2 gap-y-1 flex-wrap">
            <span>
              {mix.talked} {mix.talked === 1 ? 'conversation' : 'conversations'}
            </span>
            <span className="text-sm font-medium text-fg-secondary">
              {knocked} of {stops.length} doors
            </span>
            <InfoTip
              term={{
                title: 'Conversations',
                short:
                  'Doors where somebody answered and talked with us — including "come back later" and "do not contact", because a person had to be there to say either one.',
                long: 'Counted the same way the server counts a worked door, so this number and the coordinator\'s coverage map never disagree. A refusal is a contact but not a conversation, so it is listed separately in the mix below. There is no target: the mix is here to tell you what kind of hour you are having, not to be beaten.',
              }}
            />
            {queuedCount > 0 && (
              <span
                className="bg-status-warning-bg border border-status-warning text-status-warning text-[10px] font-bold px-2 py-0.5 rounded uppercase"
                title="Knocks saved to the device that haven't been pushed to the backend yet."
              >
                📡 {queuedCount} pending
              </span>
            )}
          </div>
          {/* The mix. Zero-count categories are dropped rather than printed
              as "0 moved" — the line stays short enough to read at a glance
              while walking, and the visible parts still sum to doors knocked. */}
          {mix.total > 0 && (
            <div className="text-xs text-fg-secondary tabular-nums">
              {[
                mix.not_home > 0 && `${mix.not_home} not home`,
                mix.refused > 0 && `${mix.refused} refused`,
                mix.moved > 0 && `${mix.moved} moved`,
              ]
                .filter(Boolean)
                .join(' · ') || 'every door answered'}
            </div>
          )}
          <div className="text-xs text-fg-muted tabular-nums">
            {[
              `${remaining} door${remaining === 1 ? '' : 's'} left`,
              // Omitted entirely when the route has no usable coordinates:
              // a guessed sunset would be believed, and a placeholder in a
              // daylight slot reads as "no time left".
              daylight && formatDaylight(daylight),
              // Held back until the sample means something — four doors in
              // the first six minutes is "40/hr" and it is noise.
              pace !== null && `${pace} doors/hr`,
              `started ${formatTime(shift.started_at)}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
          {queuedCount > 0 && (
            <button
              onClick={onRetryQueue}
              disabled={replaying}
              className="mt-1 text-[11px] bg-bg-base border border-status-warning text-status-warning hover:bg-status-warning-bg disabled:opacity-40 px-2 py-0.5 rounded"
            >
              {replaying ? 'Retrying…' : 'Retry now'}
            </button>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <button
            onClick={onEndShift}
            data-tour="stops-end"
            className="bg-status-success-bg hover:bg-status-success-bg/90 text-white font-bold px-4 py-2 rounded"
          >
            End shift →
          </button>
          <button
            onClick={() => setAbandonOpen((v) => !v)}
            className="bg-status-warning-bg hover:bg-status-warning-bg/80 text-white text-xs px-3 py-1 rounded"
          >
            Abandon
          </button>
        </div>
      </div>

      {abandonOpen && (
        <div className="bg-bg-raised rounded border border-status-warning p-4 space-y-2">
          <div className="text-sm text-status-warning">
            Abandoning the shift writes this reason into the shift record for the coordinator.
          </div>
          <input
            className="w-full rounded bg-bg-base border border-border-base px-3 py-2 text-fg-primary text-sm"
            placeholder="Why are you stopping early?"
            value={abandonReason}
            onChange={(e) => setAbandonReason(e.target.value)}
          />
          <div className="flex gap-2">
            <button
              disabled={!abandonReason.trim()}
              onClick={() => {
                onAbandon(abandonReason.trim());
                setAbandonOpen(false);
                setAbandonReason('');
              }}
              className="bg-status-warning-bg hover:bg-status-warning-bg/80 disabled:opacity-40 text-white text-sm px-3 py-1 rounded"
            >
              Confirm abandon
            </button>
            <button
              onClick={() => setAbandonOpen(false)}
              className="text-fg-secondary hover:text-fg-primary text-sm px-3 py-1"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {stops.length === 0 ? (
        <div className="bg-bg-raised rounded-lg border border-border-base p-6 text-fg-muted">
          This route has no stops.
        </div>
      ) : (
        <>
          {canShowMap && (
            <div className="flex items-center justify-between bg-bg-raised border border-border-base rounded px-3 py-2">
              <div className="text-xs text-fg-muted">
                {geocodableCount}/{stops.length} stops mapped
                {geocodableCount < stops.length && (
                  <span className="ml-2 text-status-warning">
                    ({stops.length - geocodableCount} ungeocoded — see list)
                  </span>
                )}
              </div>
              <div className="inline-flex rounded border border-border-subtle overflow-hidden text-[11px] font-semibold">
                <button
                  onClick={() => setMapMode('map')}
                  className={`px-3 py-1 ${
                    effectiveMode === 'map'
                      ? 'bg-accent text-white'
                      : 'bg-bg-base text-fg-secondary hover:bg-bg-raised'
                  }`}
                >
                  🗺 Map
                </button>
                <button
                  onClick={() => setMapMode('list')}
                  className={`px-3 py-1 ${
                    effectiveMode === 'list'
                      ? 'bg-accent text-white'
                      : 'bg-bg-base text-fg-secondary hover:bg-bg-raised'
                  }`}
                >
                  ☰ List
                </button>
              </div>
            </div>
          )}

          {/*
            Lasso banner. It is the only place the reorder is visible as a
            *state* rather than as an order, so it carries the count and the
            one-tap undo. Rendered above both surfaces, and outside the
            map/list switch, so switching views never loses the selection or
            the way back out of it.
          */}
          {lassoPolygon && (
            <div className="flex items-center justify-between gap-3 rounded border border-sky-400/60 bg-sky-400/10 px-3 py-2">
              <div className="text-xs text-fg-secondary min-w-0">
                {lassoSelection.size > 0 ? (
                  <>
                    <span className="text-fg-primary font-semibold">
                      {lassoSelection.size} stop
                      {lassoSelection.size === 1 ? '' : 's'} pulled to the front
                    </span>
                    {lassoKnockedSince > 0 && ` · ${lassoKnockedSince} knocked since`}
                    {/* Stated, not hidden: a stop with no coordinates cannot
                        be inside a polygon, so it can never be lassoed. It
                        keeps its place in the list rather than disappearing —
                        an ungeocoded door is still a real door. */}
                    {unmappedCount > 0 &&
                      ` · ${unmappedCount} unmapped ${
                        unmappedCount === 1 ? 'stop stays' : 'stops stay'
                      } in route order`}
                  </>
                ) : (
                  'No unknocked stops inside that circle.'
                )}
              </div>
              <button
                onClick={clearLasso}
                className="shrink-0 text-[11px] font-semibold bg-bg-base border border-border-base text-fg-secondary hover:text-fg-primary px-3 py-1 rounded"
              >
                Clear
              </button>
            </div>
          )}

          {effectiveMode === 'map' ? (
            <StopsMapSurface
              stops={stops}
              knockedStopIds={knockedStopIds}
              knocks={knocks}
              onPick={onPick}
              lassoArmed={lassoArmed}
              onLassoArmedChange={setLassoArmed}
              lassoPolygon={lassoPolygon}
              onLassoComplete={handleLassoComplete}
              lassoSelection={lassoSelection}
            />
          ) : (
            <ul className="divide-y divide-border-base rounded-lg border border-border-base bg-bg-raised">
              {orderedStops.map((s) => {
                const done = knockedStopIds.has(s.id);
                const acp = (s.voter.first_name || '').toUpperCase() === 'CONFIDENTIAL';
                const knock = knocks.find(k => k.route_stop_id === s.id);
                const hasPhoto = knock?.response?.photo_path;
                const lassoed = lassoSelection.has(s.id);
                return (
                  <li
                    key={s.id}
                    className={`p-3 flex items-center justify-between gap-4 ${done ? 'opacity-60' : ''} ${
                      lassoed ? 'border-l-2 border-sky-400 bg-sky-400/5' : ''
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {/* The route's own number, unchanged. The lasso
                            reorders the rows a canvasser reads; it does not
                            renumber the route, because #7 still means what
                            the coordinator meant by #7. */}
                        <span className="text-xs text-fg-muted font-mono">#{s.sequence}</span>
                        <span className="text-fg-primary font-medium truncate">
                          {acp ? '[ACP voter]' : `${s.voter.first_name} ${s.voter.last_name}`}
                        </span>
                        {done && (
                          <span className="bg-status-success-bg text-status-success text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">
                            Knocked
                          </span>
                        )}
                        {hasPhoto && (
                          <span className="text-status-warning text-sm" title="Photo captured">
                            📷
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-fg-muted truncate">{s.voter.address}</div>
                    </div>
                    <button
                      onClick={() => onPick(s)}
                      className="bg-accent hover:bg-accent-hover text-white text-sm font-semibold px-3 py-1 rounded shrink-0"
                    >
                      Knock
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StopsMapSurface — Stage 2 geographic turf visualization
// ---------------------------------------------------------------------------
//
// Renders the shift's RouteStops as a Leaflet map. Each stop becomes a
// numbered pin whose color encodes state:
//
//   - RED        → unknocked, available to tap
//   - EMERALD    → already knocked and persisted server-side
//   - AMBER      → knocked but still queued in IndexedDB (offline)
//   - SLATE-300  → ACP / confidential voter — "leave lit only", not clickable
//
// A lassoed stop keeps its state color and gains a sky ring. The lasso is
// armed only from its own control and disarms itself after one draw, so the
// default gesture here stays pan-and-tap: the reorder never competes with
// logging a door.
//
// Tapping a pin opens a minimal popup with the voter name + address and a
// "Knock this door →" CTA that routes back into the PerDoorCard flow via
// the same `onPick` prop the list surface uses. Sharing the callback means
// the two surfaces are interchangeable — the toggle is pure UX, not a
// new code path.
//
// The map auto-fits its viewport to the bounding box of the geocoded stops
// so a canvasser doesn't have to pan to find their route. If a stop is
// ungeocoded (`lat==null || lng==null`), we silently drop it from the
// markers — the list surface still shows it via the "X ungeocoded" note
// in the toggle strip above.
//
// `knockedStopIds` merges server + queued knocks (see the useMemo in the
// main component), but the popup needs to distinguish the two so we
// separately derive `queuedSet` from `knockedStopIds` minus the fresh
// server fetch — no, actually, we can't do that without a third prop. A
// simpler model: if a stop is in `knockedStopIds` it's "done"; we don't
// distinguish queued vs persisted in the pin color yet. The amber "N
// pending" badge in the StopsScreen header already tells the canvasser
// how many are offline-only, which is enough for Slice 3. A later pass
// can thread the queued set through to the map if the distinction proves
// useful in the field.
function StopsMapSurface({
  stops,
  knockedStopIds,
  knocks,
  onPick,
  lassoArmed,
  onLassoArmedChange,
  lassoPolygon,
  onLassoComplete,
  lassoSelection,
}: {
  stops: Stop[];
  knockedStopIds: Set<number>;
  knocks: KnockRead[];
  onPick: (s: Stop) => void;
  /** Lasso is armed only by its own button and disarms itself after one
   *  draw — so the default gesture on this map stays pan, and a tap on a pin
   *  stays "log this door". */
  lassoArmed: boolean;
  onLassoArmedChange: (armed: boolean) => void;
  lassoPolygon: LassoLatLng[] | null;
  onLassoComplete: (ring: LassoLatLng[]) => void;
  lassoSelection: Set<number>;
}) {
  // Filter to stops with usable coordinates and sort by sequence so the
  // polyline draws in walking order regardless of how the backend served
  // them. O(n log n) for n ≈ 30-150 stops — never hot.
  const geocodedStops = stops
    .filter(
      (s) =>
        typeof s.voter.lat === 'number' &&
        typeof s.voter.lng === 'number' &&
        // Defensive: reject (0,0) which is the geocoder's sentinel for
        // "address didn't resolve". Platte County, MO is nowhere near 0,0.
        !(s.voter.lat === 0 && s.voter.lng === 0),
    )
    .slice()
    .sort((a, b) => a.sequence - b.sequence);

  // Walking-order polyline segments. One entry per leg (N-1 legs for N
  // stops), each a two-point pair. Rendering per-segment — rather than
  // coalescing into one big polyline — lets us color "done" legs
  // (both endpoints knocked) distinctly from "upcoming" legs without
  // breakage when the canvasser logs knocks out of sequence.
  type Leg = {
    key: string;
    points: [[number, number], [number, number]];
    done: boolean;
  };
  const legs = useMemo<Leg[]>(() => {
    const out: Leg[] = [];
    for (let i = 0; i < geocodedStops.length - 1; i += 1) {
      const a = geocodedStops[i];
      const b = geocodedStops[i + 1];
      out.push({
        key: `leg-${a.id}-${b.id}`,
        points: [
          [a.voter.lat as number, a.voter.lng as number],
          [b.voter.lat as number, b.voter.lng as number],
        ],
        done: knockedStopIds.has(a.id) && knockedStopIds.has(b.id),
      });
    }
    return out;
  }, [geocodedStops, knockedStopIds]);

  // Bounding-box fit: fall back to Platte County, MO if the route is
  // entirely ungeocoded (should never happen — the parent hides the map
  // toggle in that case — but belt-and-suspenders).
  const bounds = useMemo(() => {
    if (geocodedStops.length === 0) return null;
    const lats = geocodedStops.map((s) => s.voter.lat as number);
    const lngs = geocodedStops.map((s) => s.voter.lng as number);
    return L.latLngBounds(
      [Math.min(...lats), Math.min(...lngs)],
      [Math.max(...lats), Math.max(...lngs)],
    );
  }, [geocodedStops]);

  if (geocodedStops.length === 0) {
    return (
      <div className="bg-bg-raised rounded-lg border border-border-base p-6 text-fg-muted">
        No stops on this route have been geocoded. Switch to the list view.
      </div>
    );
  }

  const center: [number, number] = bounds
    ? [bounds.getCenter().lat, bounds.getCenter().lng]
    : [39.21, -94.63];

  return (
    <div className="rounded-lg overflow-hidden border border-border-base shadow-lg">
      <div
        className="w-full"
        // Fixed height works better than aspect-ratio here because iPad
        // users rotate between portrait and landscape mid-walk and we
        // don't want the map to reflow to uselessly short.
        style={{ height: '60vh', minHeight: '400px' }}
      >
        <MapContainer
          center={center}
          zoom={15}
          bounds={bounds || undefined}
          boundsOptions={{ padding: [24, 24] }}
          style={{ width: '100%', height: '100%' }}
          className="z-0"
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap contributors"
            maxZoom={19}
          />

          <FullscreenControl />

          {/* Circle the block you are standing in; those stops jump to the
              front of the list. Nothing is written — see lassoOrder.ts. */}
          <LassoControl
            armed={lassoArmed}
            onArmedChange={onLassoArmedChange}
            polygon={lassoPolygon}
            onComplete={onLassoComplete}
          />

          {/* Walking-order polyline: one Polyline per leg so "done" and
              "upcoming" segments can use different strokes. Rendered
              before the Markers so pins paint on top. */}
          {legs.map((leg) => (
            <Polyline
              key={leg.key}
              positions={leg.points}
              pathOptions={
                leg.done
                  ? {
                      color: '#059669', // emerald-600
                      weight: 3,
                      opacity: 0.55,
                      dashArray: '4 6',
                    }
                  : {
                      color: '#dc2626', // red-600
                      weight: 4,
                      opacity: 0.85,
                    }
              }
            />
          ))}
          {geocodedStops.map((s) => {
            const done = knockedStopIds.has(s.id);
            const acp =
              (s.voter.first_name || '').toUpperCase() === 'CONFIDENTIAL';
            const color = acp
              ? '#cbd5e1' // slate-300 — visually muted; not a target
              : done
              ? '#059669' // emerald-600 — knocked
              : '#dc2626'; // red-600 — unknocked target
            // Lassoed stops keep their state color and gain a sky ring — the
            // same ink LassoControl draws with, so the halo reads as "you
            // circled this" rather than as a fifth outcome state.
            const lassoed = lassoSelection.has(s.id);
            const ring = lassoed
              ? '0 0 0 3px #38bdf8, 0 2px 5px rgba(0,0,0,0.4)'
              : '0 2px 5px rgba(0,0,0,0.4)';
            const icon = L.divIcon({
              className: 'stops-map-pin',
              html: `
                <div style="
                  background-color: ${color};
                  border: 2px solid white;
                  border-radius: 50%;
                  width: 30px;
                  height: 30px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  color: white;
                  font-size: 11px;
                  font-weight: 700;
                  box-shadow: ${ring};
                  ${done ? 'opacity: 0.75;' : ''}
                ">${s.sequence}</div>
              `,
              iconSize: [30, 30],
              iconAnchor: [15, 15],
              popupAnchor: [0, -14],
            });
            return (
              <Marker
                key={`stop-pin-${s.id}`}
                position={[s.voter.lat as number, s.voter.lng as number]}
                icon={icon}
              >
                <Popup>
                  <div className="text-sm" style={{ minWidth: '200px' }}>
                    <div className="text-[10px] font-mono text-fg-muted uppercase">
                      Stop #{s.sequence}
                    </div>
                    <div className="font-bold text-slate-900">
                      {acp
                        ? '[ACP voter — leave lit only]'
                        : `${s.voter.first_name} ${s.voter.last_name}`}
                    </div>
                    <div className="text-xs text-slate-700">
                      {s.voter.address}
                    </div>
                    {(s.voter.city || s.voter.zip_code) && (
                      <div className="text-xs text-slate-600">
                        {s.voter.city}
                        {s.voter.city && s.voter.zip_code ? ', ' : ''}
                        {s.voter.zip_code}
                      </div>
                    )}
                    {s.voter.engagement_tier && (
                      <div className="text-[10px] uppercase tracking-wide text-fg-muted mt-1">
                        {s.voter.engagement_tier}
                      </div>
                    )}
                    {done && (
                      <div className="mt-2 inline-block bg-emerald-100 text-emerald-800 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">
                        Knocked
                      </div>
                    )}
                    {(() => {
                      const knock = knocks.find(k => k.route_stop_id === s.id);
                      if (knock?.response?.photo_path) {
                        return (
                          <div className="mt-2 bg-bg-base border border-border-base rounded p-2">
                            <img
                              src={photoUrl(knock.response.photo_path)}
                              alt="Knock photo"
                              className="rounded border border-border-base max-h-20 object-contain w-full"
                            />
                          </div>
                        );
                      }
                      return null;
                    })()}
                    {!done && !acp && (
                      <button
                        onClick={() => onPick(s)}
                        className="mt-2 w-full bg-accent hover:bg-accent-hover text-white text-xs font-semibold py-1 rounded"
                      >
                        Knock this door →
                      </button>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
      {/* Legend strip — keeps the color code obvious without cluttering
          the map itself. Compact enough to fit below the 60vh map on
          a 812pt iPhone SE. */}
      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 bg-bg-base border-t border-border-base px-3 py-1.5 text-[10px] uppercase tracking-wide text-fg-secondary">
        <span className="flex items-center gap-1">
          <span
            className="inline-block w-2.5 h-2.5 rounded-full"
            style={{ background: '#dc2626' }}
          />
          Unknocked
        </span>
        <span className="flex items-center gap-1">
          <span
            className="inline-block w-2.5 h-2.5 rounded-full"
            style={{ background: '#059669' }}
          />
          Knocked
        </span>
        <span className="flex items-center gap-1">
          <span
            className="inline-block w-2.5 h-2.5 rounded-full"
            style={{ background: '#cbd5e1' }}
          />
          ACP (skip)
        </span>
        <span className="flex items-center gap-1">
          <span
            className="inline-block w-4 h-0.5"
            style={{ background: '#dc2626' }}
          />
          Walk next
        </span>
        <span className="flex items-center gap-1">
          <span
            className="inline-block w-4 h-0.5"
            style={{
              background:
                'repeating-linear-gradient(to right, #059669 0 3px, transparent 3px 6px)',
            }}
          />
          Walked
        </span>
        {/* The lasso button is an unlabeled icon in the map corner, so say
            what it does once, here, where the rest of the key already is. */}
        <span className="flex items-center gap-1 normal-case tracking-normal">
          <span
            className="inline-block w-2.5 h-2.5 rounded-full border-2"
            style={{ borderColor: '#38bdf8' }}
          />
          Lasso a block to do those stops next
        </span>
      </div>
    </div>
  );
}

function PerDoorCard({
  shiftId,
  stop,
  issues,
  slate = [],
  onCancel,
  onSaved,
  onError,
}: {
  shiftId: number;
  stop: Stop;
  issues: CampaignIssueRead[];
  /**
   * The multi-candidate slate for this walk, from `ShiftBriefing.candidates`
   * (threaded in — NEVER fetched here; a canvasser can be offline). Defaults to
   * empty so single-race walks render exactly as before.
   */
  slate?: CandidateBrief[];
  onCancel: () => void;
  onSaved: (k: KnockRead) => void;
  onError: (m: string) => void;
}) {
  const [outcome, setOutcome] = useState<KnockbackOutcome | null>(null);
  const [notes, setNotes] = useState('');
  const [answeredBy, setAnsweredBy] = useState<AnsweredBy | ''>('');
  const [topIssues, setTopIssues] = useState<string[]>([]);
  const [lean, setLean] = useState<CandidateLean | ''>('');
  // Per-candidate leans (Phase 2). Rated candidates only, keyed by id; an absent
  // key means "skipped — didn't ask" and produces NO payload entry.
  const [candidateLeans, setCandidateLeans] = useState<Record<number, SlateLean>>({});
  const [intent, setIntent] = useState<VoteIntent | ''>('');
  const [yardSign, setYardSign] = useState(false);
  const [volunteer, setVolunteer] = useState(false);
  const [donate, setDonate] = useState(false);
  const [escalate, setEscalate] = useState(false);
  const [escalateReason, setEscalateReason] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const showResponseForm = outcome === 'talked';
  const acp = (stop.voter.first_name || '').toUpperCase() === 'CONFIDENTIAL';

  // Issues are per-candidate now (candidate_id NULL = org-wide). Offer only
  // what this door can be about: org-wide issues plus the issues of candidates
  // on this walk's slate. When the slate is empty (route with no geography, or
  // briefing unavailable offline) fall back to everything — capturing a voter
  // concern beats hiding the chip.
  const slateIds = new Set(slate.map((c) => c.id));
  const doorIssues =
    slateIds.size > 0
      ? issues.filter(
          (i) => i.candidate_id === null || slateIds.has(i.candidate_id),
        )
      : issues;
  const issueOwnerName = (i: CampaignIssueRead): string | null => {
    if (i.candidate_id === null) return null;
    const owner = slate.find((c) => c.id === i.candidate_id);
    return owner ? candidateShortName(owner) : null;
  };

  function toggleIssue(slug: string) {
    setTopIssues((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  }

  // `null` clears a candidate back to "skipped" (drop the key so no entry is
  // emitted); a lean value sets/overwrites it.
  function setCandidateLean(candidateId: number, next: SlateLean | null) {
    setCandidateLeans((prev) => {
      if (next === null) {
        if (!(candidateId in prev)) return prev;
        const { [candidateId]: _drop, ...rest } = prev;
        return rest;
      }
      return { ...prev, [candidateId]: next };
    });
  }

  async function submit() {
    if (!outcome) {
      onError('Pick an outcome before submitting.');
      return;
    }
    setSaving(true);
    let response: KnockResponseCreate | undefined;
    if (showResponseForm) {
      // Per-candidate leans — ONLY the candidates actually rated (skipped =
      // omitted). Additive to the scalar `candidate_lean`, which is untouched.
      const candidate_leans = slate
        .filter((c) => candidateLeans[c.id] !== undefined)
        .map((c) => ({
          candidate_id: c.id,
          lean: candidateLeans[c.id],
          source: STEPPER_LEAN_SOURCE,
        }));
      response = {
        answered_by: (answeredBy || undefined) as AnsweredBy | undefined,
        top_issues: topIssues.length > 0 ? topIssues : undefined,
        candidate_lean: (lean || undefined) as CandidateLean | undefined,
        candidate_leans: candidate_leans.length > 0 ? candidate_leans : undefined,
        vote_intent: (intent || undefined) as VoteIntent | undefined,
        yard_sign_interest: yardSign,
        volunteer_interest: volunteer,
        donate_interest: donate,
        escalate_flag: escalate,
        escalate_reason: escalate ? escalateReason || undefined : undefined,
      };
    }
    const body: KnockCreate = {
      route_stop_id: stop.id,
      outcome,
      notes: notes || undefined,
      response,
    };
    try {
      // Resilient variant: on network failure, the payload is persisted to
      // IndexedDB and we get back a synthetic optimistic KnockRead (negative
      // id, canvasser_id=0) so the stops list can immediately stripe the
      // stop as "knocked" without waiting for the next connectivity window.
      // HTTP errors (422, 403, 409) still throw — they're real problems
      // the canvasser needs to see immediately.
      const knock = await createKnockResilient(shiftId, body);

      // Photo upload is a follow-up — it needs a real knockback_id, so
      // skip it for offline-queued knocks (synthetic negative ids).
      // The photo will be lost in that case, which is acceptable: the
      // knock data is what matters, and the canvasser can retake the
      // photo on the next visit if needed.
      if (photoFile && knock.id > 0) {
        try {
          await uploadKnockPhoto(knock.id, photoFile);
        } catch {
          // Photo upload failure is non-fatal — the knock itself already
          // saved.  Warn the canvasser but don't block the flow.
          onError('Knock saved but photo upload failed — you can retry from the stops list.');
        }
      }

      onSaved(knock);
    } catch (e) {
      onError(formatCanvassError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="bg-bg-raised rounded-lg border border-border-base p-5">
        <div className="text-xs text-fg-muted uppercase">Stage 3 — per-door card</div>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          <h2 className="text-xl font-bold text-fg-primary">
            {acp ? '[ACP voter — leave lit only]' : `${stop.voter.first_name} ${stop.voter.last_name}`}
          </h2>
          {stop.voter.engagement_tier && (
            <span className="text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-bg-base border border-border-base text-fg-secondary">
              {stop.voter.engagement_tier}
            </span>
          )}
        </div>
        <div className="text-sm text-fg-muted">{stop.voter.address}</div>
      </div>

      <div className="bg-bg-raised rounded-lg border border-border-base p-4">
        <div className="text-xs text-fg-muted uppercase tracking-wide mb-2">Outcome</div>
        <div className="grid grid-cols-2 gap-2" data-tour="card-outcomes">
          {OUTCOMES.map((o) => (
            <button
              key={o.value}
              onClick={() => setOutcome(o.value)}
              className={`${o.color} text-white font-semibold py-3 rounded ${
                outcome === o.value ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-800' : ''
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {showResponseForm && (
        <div className="bg-bg-raised rounded-lg border border-border-base p-4 space-y-4">
          <div>
            <div className="text-xs text-fg-muted uppercase tracking-wide mb-1">Who answered</div>
            <select
              className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
              value={answeredBy}
              onChange={(e) => setAnsweredBy(e.target.value as any)}
            >
              <option value="">—</option>
              <option value="voter">The voter</option>
              <option value="household_member">Household member</option>
              <option value="caretaker">Caretaker</option>
              <option value="unknown">Not sure</option>
            </select>
          </div>

          {doorIssues.length > 0 && (
            <div>
              <div className="text-xs text-fg-muted uppercase tracking-wide mb-1">Top issues (tap to toggle)</div>
              <div className="flex flex-wrap gap-2">
                {doorIssues.map((i) => {
                  const on = topIssues.includes(i.slug);
                  const owner = issueOwnerName(i);
                  return (
                    <button
                      key={i.id}
                      onClick={() => toggleIssue(i.slug)}
                      className={`text-xs font-semibold px-3 py-1 rounded border ${
                        on
                          ? 'bg-accent border-accent text-white'
                          : 'bg-bg-base border-border-base text-fg-secondary hover:bg-bg-raised'
                      }`}
                    >
                      {owner ? `${owner} · ${i.label}` : i.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-fg-muted uppercase tracking-wide mb-1">Candidate lean</div>
              <select
                className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
                value={lean}
                onChange={(e) => setLean(e.target.value as any)}
              >
                <option value="">—</option>
                <option value="strong_us">Strong us</option>
                <option value="lean_us">Lean us</option>
                <option value="undecided">Undecided</option>
                <option value="lean_them">Lean them</option>
                <option value="strong_them">Strong them</option>
                <option value="refused">Refused to say</option>
              </select>
            </div>
            <div>
              <div className="text-xs text-fg-muted uppercase tracking-wide mb-1">Vote intent</div>
              <select
                className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
                value={intent}
                onChange={(e) => setIntent(e.target.value as any)}
              >
                <option value="">—</option>
                <option value="definitely">Definitely</option>
                <option value="probably">Probably</option>
                <option value="mail_in">Mail-in</option>
                <option value="unsure">Unsure</option>
                <option value="no">No</option>
                <option value="refused">Refused</option>
              </select>
            </div>
          </div>

          {slate.length > 0 && (
            <CandidateLeanStepper
              candidates={slate}
              value={candidateLeans}
              onChange={setCandidateLean}
              disabled={saving}
            />
          )}

          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-fg-secondary text-sm">
              <input
                type="checkbox"
                checked={yardSign}
                onChange={(e) => setYardSign(e.target.checked)}
                className="h-4 w-4"
              />
              Yard sign
            </label>
            <label className="flex items-center gap-2 text-fg-secondary text-sm">
              <input
                type="checkbox"
                checked={volunteer}
                onChange={(e) => setVolunteer(e.target.checked)}
                className="h-4 w-4"
              />
              Volunteer
            </label>
            <label className="flex items-center gap-2 text-fg-secondary text-sm">
              <input
                type="checkbox"
                checked={donate}
                onChange={(e) => setDonate(e.target.checked)}
                className="h-4 w-4"
              />
              Donate
            </label>
            <label className="flex items-center gap-2 text-fg-secondary text-sm">
              <input
                type="checkbox"
                checked={escalate}
                onChange={(e) => setEscalate(e.target.checked)}
                className="h-4 w-4 accent-amber-500"
              />
              Escalate to coordinator
            </label>
          </div>

          {escalate && (
            <input
              className="w-full rounded bg-bg-base border border-status-warning px-3 py-2 text-fg-primary text-sm"
              placeholder="Why escalate? (coordinator will see this)"
              value={escalateReason}
              onChange={(e) => setEscalateReason(e.target.value)}
            />
          )}
        </div>
      )}

      <div className="bg-bg-raised rounded-lg border border-border-base p-4">
        <div className="text-xs text-fg-muted uppercase tracking-wide mb-1">Notes (optional)</div>
        <textarea
          className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Free text — anything you want to remember"
        />
      </div>

      <div className="bg-bg-raised rounded-lg border border-border-base p-4">
        <div className="text-xs text-fg-muted uppercase tracking-wide mb-1">
          Door photo (optional)
        </div>
        <div className="flex items-start gap-3">
          <label className="cursor-pointer bg-bg-elevated hover:bg-border-subtle text-fg-secondary text-sm font-semibold px-4 py-2 rounded inline-block">
            {photoFile ? 'Change photo' : 'Take / choose photo'}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                setPhotoFile(f);
                if (f) {
                  const reader = new FileReader();
                  reader.onload = () => setPhotoPreview(reader.result as string);
                  reader.readAsDataURL(f);
                } else {
                  setPhotoPreview(null);
                }
              }}
            />
          </label>
          {photoFile && (
            <button
              onClick={() => {
                setPhotoFile(null);
                setPhotoPreview(null);
              }}
              className="text-xs text-status-danger hover:text-status-danger/80 mt-2"
            >
              Remove
            </button>
          )}
        </div>
        {photoPreview && (
          <img
            src={photoPreview}
            alt="Door photo preview"
            className="mt-2 rounded border border-border-base max-h-48 object-contain"
          />
        )}
        <div className="text-[11px] text-fg-muted mt-1">
          JPEG, PNG, or WebP. Max 10 MB. EXIF/GPS metadata is stripped server-side.
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={onCancel}
          className="flex-1 bg-bg-elevated hover:bg-border-subtle text-fg-primary font-semibold py-3 rounded"
        >
          Cancel
        </button>
        <button
          disabled={!outcome || saving}
          onClick={submit}
          className="flex-1 bg-accent hover:bg-accent-hover disabled:opacity-40 text-white font-bold py-3 rounded"
        >
          {saving ? 'Saving…' : 'Save knock'}
        </button>
      </div>
    </div>
  );
}

function AutoSummaryBlock({ summary }: { summary: AARAutoSummary }) {
  return (
    <div className="bg-bg-base border border-border-base rounded p-4 space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-xs text-fg-muted uppercase">Doors attempted</div>
          <div className="text-2xl font-bold text-fg-primary">{summary.total_doors_attempted}</div>
        </div>
        <div>
          <div className="text-xs text-fg-muted uppercase">Avg min / door</div>
          <div className="text-2xl font-bold text-fg-primary">
            {summary.avg_minutes_per_door == null ? '—' : summary.avg_minutes_per_door.toFixed(1)}
          </div>
        </div>
        <div>
          <div className="text-xs text-fg-muted uppercase">Escalations</div>
          <div className="text-2xl font-bold text-status-warning">{summary.escalation_count}</div>
        </div>
      </div>

      {summary.outcomes.length > 0 && (
        <div>
          <div className="text-xs text-fg-muted uppercase mb-1">Outcomes</div>
          <div className="flex flex-wrap gap-2">
            {summary.outcomes.map((o) => (
              <span key={o.outcome} className="bg-bg-raised text-fg-secondary px-2 py-0.5 rounded text-xs">
                {o.outcome.replace('_', ' ')}: <strong className="text-fg-primary">{o.count}</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      {summary.issue_frequency.length > 0 && (
        <div>
          <div className="text-xs text-fg-muted uppercase mb-1">Issues heard</div>
          <div className="flex flex-wrap gap-2">
            {summary.issue_frequency.map((i) => (
              <span key={i.slug} className="bg-bg-raised text-fg-secondary px-2 py-0.5 rounded text-xs">
                {i.slug}: <strong className="text-fg-primary">{i.count}</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      {summary.lean_distribution.length > 0 && (
        <div>
          <div className="text-xs text-fg-muted uppercase mb-1">Candidate lean distribution</div>
          <div className="flex flex-wrap gap-2">
            {summary.lean_distribution.map((l) => (
              <span key={l.lean} className="bg-bg-raised text-fg-secondary px-2 py-0.5 rounded text-xs">
                {l.lean.replace('_', ' ')}: <strong className="text-fg-primary">{l.count}</strong>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AARScreen({
  shift,
  prefill,
  onSubmitted,
  onError,
}: {
  shift: CanvassShiftRead;
  prefill: AARPrefill | null;
  onSubmitted: (aar: AARRead) => void;
  onError: (m: string) => void;
}) {
  const [wentWell, setWentWell] = useState('');
  const [unexpected, setUnexpected] = useState('');
  const [safety, setSafety] = useState('');
  const [turf, setTurf] = useState('');
  const [saving, setSaving] = useState(false);

  // Rehydrate from any existing draft so we don't clobber on the idempotent path.
  useEffect(() => {
    if (prefill?.existing) {
      setWentWell(prefill.existing.went_well || '');
      setUnexpected(prefill.existing.unexpected || '');
      setSafety(prefill.existing.safety_issues || '');
      setTurf(prefill.existing.turf_notes || '');
    }
  }, [prefill]);

  if (prefill === null) return <Loading label="Building AAR summary…" />;

  async function submit() {
    setSaving(true);
    try {
      const aar = await submitAAR(shift.id, {
        went_well: wentWell || undefined,
        unexpected: unexpected || undefined,
        safety_issues: safety || undefined,
        turf_notes: turf || undefined,
      });
      onSubmitted(aar);
    } catch (e) {
      onError(formatCanvassError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="bg-bg-raised rounded-lg border border-border-base p-5">
        <div className="text-xs text-fg-muted uppercase">Stage 4 — after action review</div>
        <h2 className="text-2xl font-bold text-fg-primary mt-1">Shift #{shift.id} · {shift.status.replace('_', ' ')}</h2>
        <div className="text-xs text-fg-muted mt-1">
          Started {formatTime(shift.started_at)} · ended {formatTime(shift.ended_at)}
        </div>
        <div className="mt-4">
          <AutoSummaryBlock summary={prefill.auto_summary} />
        </div>
      </div>

      <div className="bg-bg-raised rounded-lg border border-border-base p-4 space-y-3">
        <TextArea label="What went well" value={wentWell} onChange={setWentWell} placeholder="Things to repeat next shift" />
        <TextArea label="Anything unexpected" value={unexpected} onChange={setUnexpected} placeholder="Surprises, weird conversations, scheduling hits" />
        <TextArea label="Safety issues" value={safety} onChange={setSafety} placeholder="Dogs, drivers, hostile encounters — be specific" />
        <TextArea label="Turf notes" value={turf} onChange={setTurf} placeholder="Missing doors, bad addresses, map corrections" />
      </div>

      <button
        disabled={saving}
        onClick={submit}
        className="w-full bg-accent hover:bg-accent-hover disabled:opacity-40 text-white font-bold py-4 rounded-lg text-lg"
      >
        {saving ? 'Submitting…' : 'Submit AAR →'}
      </button>
    </div>
  );
}

function TextArea({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div>
      <div className="text-xs text-fg-muted uppercase tracking-wide mb-1">{label}</div>
      <textarea
        className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
        rows={2}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function DoneScreen({ aar, onBackHome }: { aar: AARRead; onBackHome: () => void }) {
  return (
    <div className="bg-bg-raised rounded-lg border border-border-base p-6 text-center space-y-3">
      <div className="text-4xl">🎉</div>
      <h2 className="text-2xl font-bold text-fg-primary">AAR submitted</h2>
      <div className="text-fg-muted text-sm">
        Shift #{aar.canvass_shift_id} is locked in. Coordinator can now sign off.
      </div>
    </div>
  );
}
